/**
 * The local command grammar — the deterministic half of conversational input.
 *
 * ## Why this exists at all
 *
 * The player's hands are on the keys. Anything they say or type has to land before they
 * lose the thread of what they were playing, and an iPad beside an acoustic piano is
 * frequently on flaky wifi or none at all. So the phrases that carry the most weight —
 * "take me back two chords", "save that", "what key are we in?", "simpler" — are matched
 * here, with **zero network**, and dispatched straight into the reducer.
 *
 * Everything this file does not recognise falls through to `/api/command`, where a fast
 * model turns it into a `CommandIntent`. Both paths converge on `applyIntent`, so a
 * locally-matched "save that" and a model-returned `{ kind: 'save' }` do exactly the
 * same thing.
 *
 * ## The one intent that is local-only
 *
 * `CommandIntent` has no `step-back`: stepping the path back is a pure client-side graph
 * operation and there is no reason for it to make a round trip. `LocalIntent` is
 * therefore `CommandIntent` plus that one case, and `applyIntent` accepts the superset.
 *
 * ## Mood without a mode switch
 *
 * `adjust-mood` ("make it darker", "less jazzy") does not open a dialog and does not
 * replace the intention. It appends a clause to a rolling, bounded, latest-wins
 * intention string — so saying "darker" and then, four chords later, "actually brighter"
 * leaves one clause, the newer one, and the player never had to find a settings screen.
 * See `appendMood`.
 */

import {
  type CommandIntent,
  type KeyEstimate,
  type Perspective,
  type SessionAction,
  type SessionState,
  noteName,
  spellPitchClass,
} from '@hc/shared';

// ---------------------------------------------------------------------------
// Intents
// ---------------------------------------------------------------------------

/** Stepping the path back never needs the network, so it is not part of `CommandIntent`. */
export interface StepBackIntent {
  kind: 'step-back';
  count: number;
}

/** Everything the model can return, plus the purely local `step-back`. */
export type LocalIntent = CommandIntent | StepBackIntent;

/**
 * The port to Chunk E's `/api/command` client. Declared as a function type rather than
 * imported so this module stays testable without a network and without a fetch stub.
 */
export type SendCommand = (text: string, context: CommandRequestContext) => Promise<CommandIntent>;

/** What the remote command endpoint needs to disambiguate an utterance. */
export interface CommandRequestContext {
  sessionId: string;
  perspectives: Perspective[];
  adventurousness: number;
  intention?: string;
  keyEstimate: KeyEstimate;
  /** Most recent last, display symbols only — enough for "why did that work?". */
  recentSymbols: string[];
}

// ---------------------------------------------------------------------------
// Tunables
// ---------------------------------------------------------------------------

/** How far one "simpler" / "more surprising" moves the slider. */
export const ADVENTUROUSNESS_NUDGE = 0.15;

/** Below this the key estimate is not worth reporting as fact. */
export const KEY_CONFIDENCE_FLOOR = 0.25;

/** Below this, the answer hedges with the runner-up. */
export const KEY_CONFIDENCE_HEDGE = 0.6;

/** Separates the player's own intention from the mood clauses appended to it. */
export const MOOD_SEPARATOR = ' · ';

/** The rolling window. Older clauses fall off the front. */
export const MAX_MOOD_CLAUSES = 3;

/** Hard ceiling on the whole intention string, so the prompt cannot grow without bound. */
export const MAX_INTENTION_CHARS = 200;

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

const CONTRACTIONS: ReadonlyArray<[RegExp, string]> = [
  [/\bwhats\b/g, 'what is'],
  [/\bwheres\b/g, 'where is'],
  [/\bhows\b/g, 'how is'],
  [/\bthats\b/g, 'that is'],
  [/\bim\b/g, 'i am'],
  [/\bive\b/g, 'i have'],
  [/\bdont\b/g, 'do not'],
  [/\bcant\b/g, 'can not'],
  [/\bwont\b/g, 'will not'],
  [/\blets\b/g, 'let us'],
  [/\bgimme\b/g, 'give me'],
  [/\bwanna\b/g, 'want to'],
];

/**
 * Lowercase, drop apostrophes, expand the handful of contractions that matter, strip
 * everything that is not a letter, digit or space, and collapse whitespace.
 *
 * Speech recognition and a soft keyboard produce wildly different punctuation for the
 * same sentence, so every pattern below is written against this normal form only.
 */
export function normalizeUtterance(input: string): string {
  let text = input
    .toLowerCase()
    .replace(/[‘’']/g, '')
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  for (const [pattern, replacement] of CONTRACTIONS) text = text.replace(pattern, replacement);
  return text.replace(/\s+/g, ' ').trim();
}

const NUMBER_WORDS: Readonly<Record<string, number>> = Object.freeze({
  a: 1,
  an: 1,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  couple: 2,
  few: 3,
});

/**
 * Homophones dictation reliably produces for small numbers. Only accepted when a unit
 * noun follows ("back to chords"), never on their own — otherwise "go back to the start"
 * would be read as "go back 2".
 */
const NUMBER_HOMOPHONES: Readonly<Record<string, number>> = Object.freeze({
  to: 2,
  too: 2,
  for: 4,
  ate: 8,
});

/** Largest step-back this grammar will produce. Guards against "back 9999 chords". */
export const MAX_STEP_BACK = 32;

function wordValue(token: string, includeHomophones: boolean): number | undefined {
  if (/^\d+$/.test(token)) return Number.parseInt(token, 10);
  return NUMBER_WORDS[token] ?? (includeHomophones ? NUMBER_HOMOPHONES[token] : undefined);
}

/**
 * Pull a count out of a step-back phrase. Handles digits and number words, the
 * `a couple (of)` / `a few` idioms, and the bare verb (which means one).
 */
export function parseStepCount(text: string): number {
  const unit = '(?:chords?|steps?|moves?|bars?|places?)';
  const words = Object.keys(NUMBER_WORDS).join('|');
  const withHomophones = [...Object.keys(NUMBER_WORDS), ...Object.keys(NUMBER_HOMOPHONES)].join(
    '|',
  );

  const patterns: ReadonlyArray<readonly [RegExp, boolean]> = [
    // "…back two chords", "…back 3 steps", "…back a couple of chords"
    [new RegExp(`\\b(\\d{1,4}|${withHomophones})\\b(?:\\s+of)?\\s+${unit}\\b`), true],
    // "undo the last two"
    [new RegExp(`\\blast\\s+(?:a\\s+)?(\\d{1,4}|${words})\\b`), false],
    // "…back a couple", "rewind two", "undo 3" — no unit named.
    [
      new RegExp(
        `\\b(?:back|rewind|undo)(?:\\s+up)?\\s+(?:by\\s+)?(?:a\\s+)?(\\d{1,4}|${words})\\b`,
      ),
      false,
    ],
  ];

  for (const [pattern, homophones] of patterns) {
    const match = pattern.exec(text);
    if (!match) continue;
    const value = wordValue(match[1] as string, homophones);
    if (value === undefined || !Number.isFinite(value) || value < 1) continue;
    return Math.min(value, MAX_STEP_BACK);
  }
  return 1;
}

// ---------------------------------------------------------------------------
// The grammar
// ---------------------------------------------------------------------------

const STEP_BACK =
  /\b(?:take me back|takes me back|take us back|go back|goes back|going back|step back|steps back|back up|rewind|undo|scrub back|move back)\b/;

const SAVE = /\b(?:save|bookmark|keep|remember|capture|note down|write down|hang on to)\b/;
const SAVE_OBJECT =
  /\b(?:that|this|it|those|these|them|the progression|the whole thing|the last bit|the transition|the sketch|the lot)\b/;
const SAVE_TRANSITION =
  /\b(?:transition|that move|that change|that shift|those two chords|last two chords|the move|that step)\b/;
const SAVE_PROGRESSION =
  /\b(?:progression|whole thing|everything|all of (?:it|that|this)|the lot|sequence|the sketch|the session)\b/;

const KEY_QUESTION = /^(?:what|which|tell me (?:the|what))\b[a-z0-9 ]*\bkey\b/;
const KEY_QUESTION_BLOCKERS = /\b(?:next|should|modulate|go to|change to|move to|end up)\b/;

/**
 * Adventurousness nudges. **Order matters** — negated forms come first, so "less
 * surprising" is read as a step toward the familiar rather than matching "surprising".
 */
const ADVENTUROUSNESS: ReadonlyArray<readonly [RegExp, number]> = [
  // --- toward the familiar ---
  [
    /\bless\s+(?:adventurous|surprising|weird|wild|out there|unexpected|crazy|complex|complicated|strange|exotic)\b/,
    -1,
  ],
  [/\bnot so\s+(?:weird|adventurous|crazy|out there|strange|wild)\b/, -1],
  [/\bmore\s+(?:familiar|obvious|conventional|predictable|traditional|normal|plain)\b/, -1],
  [/\bfewer\s+(?:options|choices|ideas|surprises|branches|suggestions)\b/, -1],
  [/\b(?:simpler|simplify|simplest)\b/, -1],
  [/\b(?:keep|make|something|play) it simple\b/, -1],
  [/\bkeep it (?:safe|easy|plain|familiar)\b/, -1],
  [
    /\b(?:safer|play it safe|tone it down|tone that down|dial it back|dial it down|calm it down|rein it in|settle down|back off)\b/,
    -1,
  ],
  [/\bstay (?:in|inside) the key\b/, -1],
  // --- toward the surprising ---
  [
    /\bmore\s+(?:adventurous|surprising|unexpected|interesting|unusual|exotic|daring|colourful|colorful|options|choices|ideas|branches|suggestions|spice|colour|color)\b/,
    1,
  ],
  [/\bmore out there\b/, 1],
  [/\bless\s+(?:obvious|predictable|safe|familiar|conventional|boring|plain|traditional)\b/, 1],
  [
    /\b(?:surprise me|weirder|spicier|bolder|be bolder|go wild|wilder|braver|be brave|take a risk|take more risks|push it|go further)\b/,
    1,
  ],
];

const EXPLAIN =
  /\b(?:explain|why (?:does|did|do) (?:that|this|it)|why (?:that|this) works?|what (?:just )?happened|what is going on|how does that work|tell me (?:more|about that|why))\b/;
const EXPLAIN_CURRENT = /\b(?:this|that|the current|current) chord\b/;

const SHOW_VOICING =
  /\b(?:voicing|show (?:me )?(?:the )?notes|how (?:do|should) i (?:voice|play) (?:that|this|it)|what notes)\b/;

const MARK_LIKED =
  /\b(?:i like (?:that|this|it)|i love (?:that|this|it)|like that|love that|that is (?:nice|lovely|great|good|beautiful|gorgeous|the one)|that was (?:nice|lovely|great|good)|nice one|ooh nice|lovely)\b/;

const REGENERATE =
  /\b(?:regenerate|refresh|try again|show me (?:something|some) else|something else|(?:new|different|other) (?:options|suggestions|ideas|branches|choices))\b/;

/**
 * Match an utterance against the deterministic grammar.
 *
 * Returns `null` when nothing matches — the caller should then fall through to
 * `/api/command`. Returning `null` is the normal, expected outcome for most of what a
 * player says; this grammar deliberately covers a small set very reliably rather than a
 * large set approximately.
 */
export function matchLocalCommand(input: string): LocalIntent | null {
  const text = normalizeUtterance(input);
  if (!text) return null;

  // Adventurousness is tested first because "keep it simple" and "keep that" collide on
  // the verb: both contain "keep it". The nudge phrases are the more specific pattern,
  // so they win, and "keep that" still reaches the save branch below.
  for (const [pattern, direction] of ADVENTUROUSNESS) {
    if (pattern.test(text)) {
      return { kind: 'set-adventurousness', delta: direction * ADVENTUROUSNESS_NUDGE };
    }
  }

  // Save before step-back, so "save that and take me back" keeps the chord it is about
  // to save rather than stepping off it first.
  if (SAVE.test(text) && (SAVE_OBJECT.test(text) || SAVE_PROGRESSION.test(text))) {
    return { kind: 'save', what: SAVE_TRANSITION.test(text) ? 'last-transition' : 'progression' };
  }

  if (STEP_BACK.test(text)) {
    return { kind: 'step-back', count: parseStepCount(text) };
  }

  if (KEY_QUESTION.test(text) && !KEY_QUESTION_BLOCKERS.test(text)) {
    // Answered locally from `keyEstimate`; `answer` carries no text until `applyIntent`
    // fills it in, because only the caller has the session state.
    return { kind: 'answer', text: '' };
  }

  if (MARK_LIKED.test(text)) return { kind: 'mark-liked', target: 'last-transition' };
  if (SHOW_VOICING.test(text)) return { kind: 'show-voicing' };
  if (EXPLAIN.test(text)) {
    return {
      kind: 'explain',
      target: EXPLAIN_CURRENT.test(text) ? 'current-chord' : 'last-transition',
    };
  }
  if (REGENERATE.test(text)) return { kind: 'regenerate-branches' };

  return null;
}

/** True when `matchLocalCommand` recognised the utterance as the "what key" question. */
export function isKeyQuestion(input: string): boolean {
  const text = normalizeUtterance(input);
  return KEY_QUESTION.test(text) && !KEY_QUESTION_BLOCKERS.test(text);
}

// ---------------------------------------------------------------------------
// Mood → intention
// ---------------------------------------------------------------------------

/**
 * Mood words grouped by the axis they move along. Two clauses on the same axis cannot
 * both be true, so the newer one replaces the older — that is what "latest-wins" means
 * here. Words on different axes accumulate, up to `MAX_MOOD_CLAUSES`.
 */
const MOOD_AXES: ReadonlyArray<readonly [string, readonly string[]]> = [
  [
    'brightness',
    ['dark', 'darker', 'darkest', 'gloomy', 'moody', 'bright', 'brighter', 'lighter', 'sunnier'],
  ],
  ['genre-jazz', ['jazzy', 'jazzier', 'jazz']],
  ['genre-classical', ['classical', 'baroque', 'romantic']],
  ['genre-pop', ['poppy', 'poppier', 'pop']],
  [
    'density',
    [
      'simple',
      'simpler',
      'sparse',
      'sparser',
      'busy',
      'busier',
      'denser',
      'dense',
      'lush',
      'richer',
      'rich',
    ],
  ],
  ['warmth', ['warm', 'warmer', 'cold', 'colder', 'icy', 'chilly']],
  [
    'affect',
    ['sad', 'sadder', 'happy', 'happier', 'melancholy', 'wistful', 'hopeful', 'triumphant'],
  ],
  [
    'tension',
    ['tense', 'tenser', 'tension', 'unsettled', 'calm', 'calmer', 'restful', 'resolved', 'settled'],
  ],
  ['energy', ['driving', 'urgent', 'energetic', 'gentle', 'still', 'floaty', 'dreamy', 'languid']],
];

/** The axis a mood clause moves along, or `null` when it is not one we know. */
export function moodAxis(description: string): string | null {
  const words = new Set(normalizeUtterance(description).split(' '));
  for (const [axis, terms] of MOOD_AXES) {
    for (const term of terms) if (words.has(term)) return axis;
  }
  return null;
}

/** Split a composed intention back into the player's own text and the mood clauses. */
export function splitIntention(intention: string | undefined): { base: string; moods: string[] } {
  if (!intention) return { base: '', moods: [] };
  const parts = intention.split(MOOD_SEPARATOR).map((part) => part.trim());
  const base = parts.shift() ?? '';
  return { base, moods: parts.filter(Boolean) };
}

/** Join a base intention and its mood clauses back into one string. */
export function composeIntention(base: string, moods: readonly string[]): string {
  return [base.trim(), ...moods.map((mood) => mood.trim())].filter(Boolean).join(MOOD_SEPARATOR);
}

/**
 * Append a mood clause to the running intention: **rolling** (at most
 * `MAX_MOOD_CLAUSES`), **bounded** (at most `MAX_INTENTION_CHARS`) and **latest-wins**
 * (a new clause on an axis removes the old one on that axis, and an exact repeat just
 * moves to the end rather than duplicating).
 */
export function appendMood(intention: string | undefined, description: string): string {
  const clause = description
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/[.!]+$/, '');
  if (!clause) return intention ?? '';

  const { base, moods } = splitIntention(intention);
  const axis = moodAxis(clause);
  const lowered = clause.toLowerCase();

  const kept = moods.filter((existing) => {
    if (existing.toLowerCase() === lowered) return false;
    if (axis !== null && moodAxis(existing) === axis) return false;
    return true;
  });

  kept.push(clause);
  while (kept.length > MAX_MOOD_CLAUSES) kept.shift();

  let composed = composeIntention(base, kept);
  while (composed.length > MAX_INTENTION_CHARS && kept.length > 1) {
    kept.shift();
    composed = composeIntention(base, kept);
  }
  return composed.length > MAX_INTENTION_CHARS
    ? composed.slice(0, MAX_INTENTION_CHARS).trim()
    : composed;
}

// ---------------------------------------------------------------------------
// Answering the key question
// ---------------------------------------------------------------------------

/**
 * A one-line, speakable answer to "what key are we in?".
 *
 * Honest about low confidence: three chords of planing fourths have no tonic, and
 * saying "E♭ major" with a straight face in that situation is worse than saying nothing.
 */
export function describeKey(key: KeyEstimate): string {
  if (key.confidence < KEY_CONFIDENCE_FLOOR) {
    return 'Not enough yet to call a key.';
  }
  const name = keyName(key.tonicPc, key.mode);
  if (key.confidence < KEY_CONFIDENCE_HEDGE && key.secondBest) {
    return `${name} — or possibly ${keyName(key.secondBest.tonicPc, key.secondBest.mode)}.`;
  }
  return `${name}.`;
}

/** "E♭ minor" for a tonic pitch class and mode, spelled from that key's own point of view. */
export function keyName(tonicPc: KeyEstimate['tonicPc'], mode: KeyEstimate['mode']): string {
  return `${noteName(spellPitchClass(tonicPc, { tonicPc, mode }))} ${mode}`;
}

// ---------------------------------------------------------------------------
// Execution
// ---------------------------------------------------------------------------

/**
 * Everything an intent might need to reach. Session mutation goes through `dispatch`
 * with the frozen `SessionAction` union; anything that is not a state change (opening
 * the save sheet, asking for new branches, speaking) is a callback, so this module
 * never touches the DOM, the network or IndexedDB.
 */
export interface CommandEnv {
  state: SessionState;
  dispatch(action: SessionAction): void;
  /** Quiet, transient feedback. Never a blocking dialog — the player is mid-phrase. */
  toast(message: string): void;
  /** Open the save flow at the given scope. */
  openSave(what: 'last-transition' | 'progression'): void;
  /** Ask for a fresh branch set. `hint` steers that one request and nothing after it. */
  requestBranches?(hint?: string): void;
  showExplanation?(target: 'last-transition' | 'current-chord'): void;
  showVoicing?(): void;
  markLiked?(): void;
  /** Short spoken answer. Off by default; the settings toggle decides whether this is set. */
  speak?(text: string): void;
}

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/** Human-readable label for the adventurousness slider, used in the nudge toast. */
export function adventurousnessLabel(value: number): string {
  if (value < 0.2) return 'very familiar';
  if (value < 0.4) return 'familiar';
  if (value < 0.6) return 'balanced';
  if (value < 0.8) return 'surprising';
  return 'very surprising';
}

/**
 * Run one intent. Returns the message that was surfaced to the player, so callers (and
 * tests) can assert on the outcome without reaching into the toast implementation.
 */
export function applyIntent(intent: LocalIntent, env: CommandEnv): string {
  switch (intent.kind) {
    case 'step-back': {
      const available = Math.max(0, env.state.path.length - 1);
      if (available === 0) {
        return say(env, 'Nothing to step back to yet.');
      }
      const count = Math.min(intent.count, available);
      env.dispatch({ type: 'STEP_BACK', count });
      return say(env, count === 1 ? 'Back one chord.' : `Back ${count} chords.`);
    }

    case 'save': {
      env.openSave(intent.what);
      return say(env, intent.what === 'last-transition' ? 'Saving that move…' : 'Saving…');
    }

    case 'answer': {
      // A locally-matched key question arrives with empty text; fill it in here, where
      // the session state is in hand.
      const text = intent.text.trim() || describeKey(env.state.keyEstimate);
      env.speak?.(text);
      return say(env, text);
    }

    case 'set-adventurousness': {
      const next = clamp01(env.state.adventurousness + intent.delta);
      env.dispatch({ type: 'SET_ADVENTUROUSNESS', value: next });
      return say(env, `Now ${adventurousnessLabel(next)}.`);
    }

    case 'set-perspectives': {
      const next = new Set<Perspective>(env.state.perspectives);
      for (const perspective of intent.add) next.add(perspective);
      for (const perspective of intent.remove) next.delete(perspective);
      const perspectives = [...next];
      env.dispatch({ type: 'SET_PERSPECTIVES', perspectives });
      return say(
        env,
        perspectives.length ? `Lenses: ${perspectives.join(', ')}.` : 'No lens bias.',
      );
    }

    case 'adjust-mood': {
      const intention = appendMood(env.state.intention, intent.description);
      env.dispatch({ type: 'SET_INTENTION', intention });
      return say(env, `Leaning ${intent.description.trim()}.`);
    }

    case 'regenerate-branches': {
      env.requestBranches?.(intent.hint);
      // Saying the steer back is the only evidence the player gets that the specific
      // thing they asked for was heard rather than merely counted as "again".
      const hint = intent.hint?.trim();
      return say(env, hint ? `Fresh options — ${hint}…` : 'Fresh options…');
    }

    case 'explain': {
      env.showExplanation?.(intent.target);
      return say(env, '');
    }

    case 'show-voicing': {
      env.showVoicing?.();
      return say(env, '');
    }

    case 'mark-liked': {
      env.markLiked?.();
      return say(env, 'Noted.');
    }

    case 'unclear':
      return say(env, 'Sorry — I did not catch that.');
  }
}

/** Toast unless the message is empty (some intents are their own feedback). */
function say(env: CommandEnv, message: string): string {
  if (message) env.toast(message);
  return message;
}

/** Build the context payload the remote command endpoint needs. */
export function commandContext(state: SessionState, recent = 8): CommandRequestContext {
  const context: CommandRequestContext = {
    sessionId: state.sessionId,
    perspectives: [...state.perspectives],
    adventurousness: state.adventurousness,
    keyEstimate: state.keyEstimate,
    recentSymbols: state.path.slice(-recent).map((node) => node.chord.symbol),
  };
  if (state.intention !== undefined) context.intention = state.intention;
  return context;
}

/**
 * The whole input path: match locally, else ask the server, else say so.
 *
 * A failed request is not an error the player should have to deal with — the local
 * grammar still works, so the honest response is a one-line "offline" toast and no
 * state change at all.
 */
export async function handleUtterance(
  input: string,
  env: CommandEnv,
  sendCommand?: SendCommand,
): Promise<LocalIntent> {
  const local = matchLocalCommand(input);
  if (local) {
    applyIntent(local, env);
    return local;
  }

  if (!sendCommand) {
    const intent: LocalIntent = { kind: 'unclear' };
    applyIntent(intent, env);
    return intent;
  }

  // Only the request lives in the try. A callback throwing while the intent is APPLIED
  // is not a network problem, and reporting it as "Offline" blames the connection for a
  // request that succeeded — the misdiagnosis then sends the player checking their wifi
  // instead of reporting the actual bug.
  let intent: CommandIntent;
  try {
    intent = await sendCommand(input, commandContext(env.state));
  } catch {
    env.toast('Offline — only the simple commands work right now.');
    return { kind: 'unclear' };
  }
  applyIntent(intent, env);
  return intent;
}
